import { useNavigate, useParams , Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InvitePeek } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { Wordmark } from "../components/Wordmark";
import { authClient } from "../lib/auth";
import { Avatar, Spinner, EmptyState } from "../components/ui";
import { Cover } from "../components/BookBits";
import { GenrePills } from "../components/ClubHome";
import { useWelcomeGate } from "./Welcome";
import { SignInButtons } from "../components/SignIn";
import { invites } from "@pagemates/shared/copy";

const txt = invites.join;

/*
 * The invitation (PLAN.md §Join page). Being handed the link should feel
 * like being handed the club: name, who sent it, what they're reading, the
 * shelf so far, how they work, one button. A club with no history shows what
 * the app is instead of placeholders.
 */
const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const cadence = (d: number) => d < 10 ? txt.everyWeek : d < 18 ? txt.everyTwoWeeks : d < 25 ? txt.everyThreeWeeks : d < 40 ? txt.everyMonth : txt.everyWeeks(Math.round(d / 7));

export function Join() {
  const { code } = useParams({ from: "/join/$code" });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = authClient.useSession();
  useWelcomeGate(); // a brand-new account sets up its card first, then comes back here
  const peek = useQuery({ queryKey: ["invite", code], queryFn: () => api.peekInvite(code), retry: false });
  const accept = useMutation({
    mutationFn: () => api.acceptInvite(code),
    onSuccess: async ({ clubSlug }) => {
      await qc.invalidateQueries({ queryKey: ["me"] });
      void navigate({ to: "/clubs/$clubId", params: { clubId: clubSlug }, replace: true });
    },
  });
  const signOut = useMutation({ mutationFn: () => authClient.signOut() });

  if (peek.isPending) return <main className="page landing"><Spinner label={txt.checkingYourInvite} /></main>;
  // Expired, revoked, used up or wrong: one message, and nothing about the club.
  if (peek.isError) return (
    <main className="page landing join-dead">
      <Wordmark size="md" />
      <EmptyState title={txt.thisInviteIsNo}
        action={<Link to={session.data ? "/clubs" : "/"} className="btn primary">{session.data ? txt.goToYourClubs : txt.whatIsPagemates}</Link>}>{txt.askWhoeverSentIt}</EmptyState>
    </main>
  );
  const p = peek.data;
  const dead = p.status !== "valid";
  const fresh = !p.current && p.finished.count === 0;

  const cta = dead ? (
    <div className="jrules dead">
      <span className="k">{{ expired: txt.thisLinkHasExpired, revoked: txt.thisLinkWasTurned, used: txt.thisLinkWasAlready }[p.status as "expired" | "revoked" | "used"]}</span>{txt.invitesLastAWhile}</div>
  ) : session.data ? (
    <div className="signin">
      <button className="btn primary block" disabled={accept.isPending} onClick={() => accept.mutate()}>{accept.isPending ? txt.joining : txt.joinAs(session.data.user.name)}</button>
      <p className="muted small center">{txt.signedInAs}<b>{session.data.user.name}</b>{txt.notYou}<button type="button" className="linkish small" onClick={() => signOut.mutate()}>{txt.signOut}</button></p>
      {accept.isError && <p className="error small center">{describeError(accept.error)}</p>}
    </div>
  ) : (
    <SignInButtons next={`/join/${code}`} verb={txt.continueWith} disabled={session.isPending} />
  );

  return (
    <main className="page join">
      <div className="jgrid">
        <div className="jleft">
          <Wordmark size="sm" />
          <Invite p={p} dead={dead} fresh={fresh} />
          {!dead && p.current && (
            <div className="jnow">
              <Cover book={{ hardcoverId: 0, title: p.current.title, authors: p.current.authors, coverUrl: p.current.coverUrl, pages: p.current.pages, releaseYear: null }} size="lg" />
              <div style={{ minWidth: 0 }}>
                <div className="k">{txt.readingNow}</div>
                <div className="t">{p.current.title}</div>
                <div className="a">{p.current.authors.join(", ")}{p.current.pages ? txt.pages(p.current.pages) : ""}</div>
                <div className="d">{[p.current.startedAt ? txt.started(day(p.current.startedAt)) : null, p.nextMeetingAt ? txt.meeting(day(p.nextMeetingAt)) : null].filter(Boolean).join(" · ")}</div>
              </div>
            </div>
          )}
          {!dead && p.finished.count > 0 && (
            <div className="jshelf">
              <div className="k"><span>{txt.theShelfSoFar}</span><span>{p.finished.count} {p.finished.count === 1 ? txt.book : txt.books}{p.finished.sinceYear ? txt.since(p.finished.sinceYear) : ""}</span></div>
              <div className="jplank">
                {p.finished.covers.slice(0, 5).map((b, i) => <Cover key={i} book={{ hardcoverId: i, title: b.title, authors: [], coverUrl: b.coverUrl, pages: null, releaseYear: null }} size="sm" />)}
                {p.finished.count > 5 && <span className="cover sm more">+{p.finished.count - 5}</span>}
              </div>
            </div>
          )}
          {!dead && fresh && (
            <div className="jfresh">
              <div className="k">{txt.justGettingStarted}</div>
              <p>{txt.noBookOnThe}</p>
              <ul>
                <li><b>{txt.seeWhereEveryoneIs}</b>{txt.inTheBookWithout}</li>
                <li><b>{txt.talkAsYouRead}</b>{txt.messagesPastYourSpot}</li>
                <li><b>{txt.meetWithAnAgenda}</b>{txt.theClubBuildsOn}</li>
              </ul>
            </div>
          )}
          {!dead && <div className="mobile-only"><Facts p={p} /></div>}
          {!dead && p.rules && <div className="jrules"><span className="k">{txt.howThisClubWorks}</span>{p.rules}</div>}
          {dead && <p className="muted small"><a href="/">{txt.whatIsPagemates}</a></p>}
          <div className="jcta mobile-only">{cta}</div>
        </div>
        <aside className="jright">
          {!dead && <Facts p={p} />}
          <div className="jcta">{cta}</div>
        </aside>
      </div>
    </main>
  );
}

function Invite({ p, dead, fresh }: { p: InvitePeek; dead: boolean; fresh: boolean }) {
  return (
    <div className="jinv">
      <div className="eyebrow">{dead ? txt.youWereInvitedTo : txt.youReInvitedTo}</div>
      <h1 className="name">{p.clubName}</h1>
      {p.description && <p className="tag">{p.description}</p>}
      {(p.genres?.length ?? 0) > 0 && <GenrePills genres={p.genres!} />}
      {!dead && (
        <div className="by">
          {p.invitedBy && <Avatar name={p.invitedBy.name} image={p.invitedBy.image} />}
          <span>{p.invitedBy ? <><b>{p.invitedBy.name}</b>{txt.sentYouThis}</> : null}{p.memberCount} {p.memberCount === 1 ? txt.member : txt.members}{fresh ? txt.justGettingStarted2 : ""}</span>
        </div>
      )}
    </div>
  );
}

function Facts({ p }: { p: InvitePeek }) {
  const rows: [string, string][] = [];
  if (p.memberNames.length > 0) rows.push([txt.members2, `${p.memberCount} · ${p.memberNames.join(", ")}${p.memberCount > p.memberNames.length ? "…" : ""}`]);
  if (p.usualPace) rows.push([txt.pace, txt.aboutPagesADay(p.usualPace)]);
  if (p.cadenceDays || p.nextMeetingAt) rows.push([txt.meets, [p.cadenceDays ? cadence(p.cadenceDays) : null, p.nextMeetingAt ? txt.next(day(p.nextMeetingAt)) : null].filter(Boolean).join(" · ")]);
  rows.push([txt.nextBook, { admins: txt.theOrganizerPicks, vote: txt.pickedByVote, turns: txt.membersTakeTurns }[p.pickPolicy]]);
  rows.push([txt.spoilers, p.spoilerGating ? txt.sealedByWhereYou : txt.noSealingReadAt]);
  return (
    <div className="jfacts">
      {rows.map(([k, v]) => <div key={k} className="f"><span>{k}</span><b>{v}</b></div>)}
    </div>
  );
}
